import type { SystemVersionDTO } from "@repo/codegen/teashop-base-api"
import type { ShiftStatus } from "./shiftStatus"
import type { OAuthClient } from "@/api/login"
import type {
  ILoginRequestData,
  LoginUserInfo,
  MerchantUserType,
  RoleType,
  ShopInfoType,
} from "@/api/login/types/login"

import type { PermissionType, RoleCreateType } from "@/api/user/types/worker"
import type { OrganizationScope } from "@/types/organization-scope"
import type { CallerHandledAuthFailureOptions } from "@/utils/callerHandledAuthFailure"
import type { CheckoutReceiptFooterConfig } from "@/utils/checkoutReceiptFooter"
import type { LocalPrinterStyleConfig } from "@/utils/localPrinterReceiptStyle"
import type { ReceiptPrintTargetType } from "@/utils/receiptPrintTarget"
import type { PersistedRoleViewValue, RoleViewPreferenceV2 } from "@/utils/roleViewPreference"
import { ElMessage } from "element-plus"
import { defineStore } from "pinia"
import { computed, ref } from "vue"
import {
  getShopInfoApi,
  getUserRolesApi,
  loginApi,
  logoutApi,
  refreshTokenApi,
  wxLoginApi,
} from "@/api/login"
import {
  isHeadquartersScopeRoleUnavailableError,
  switchOrganizationScope,
} from "@/api/organization-scope"
import { getNowBussinessDate } from "@/api/setting/cashierSettings"
import { getPermissionListAll, getRoleDetailListByShopId } from "@/api/user/worker"
import asyncRouteSettings from "@/config/async-route"
import router, { resetRouter } from "@/router"
import { isHqScope } from "@/router/hqAccess"
import store from "@/store"
import { useChainScopeStore } from "@/store/modules/chainScope"
import { recordBusinessContextTrace } from "@/utils/businessContext/diagnostics"
import { invalidateBusinessContextRuntime } from "@/utils/businessContext/invalidation"
import {
  type BusinessContextTransaction,
  coordinateBusinessContextRefresh,
  refreshPermissionSession,
  switchBusinessContext,
} from "@/utils/businessContext/manager"
import { fetchOrganizationsForCurrentSession } from "@/utils/businessContext/organizations"
import { invalidateSystemVersionRuntime } from "@/utils/businessContext/systemVersion"
import {
  getRefreshToken,
  getToken,
  removeRefreshToken,
  removeToken,
  setRefreshToken,
  setToken,
} from "@/utils/cache/cookies"
import { resolveCheckoutReceiptFooterConfig } from "@/utils/checkoutReceiptFooter"
import { normalizeReceiptPrintTarget } from "@/utils/receiptPrintTarget"
import { resolveRoleDefaultHomePath, resolveRoleProfile } from "@/utils/roleProfile"
import { clearRoleViewPreference, readRoleViewPreference, saveRoleViewPreference } from "@/utils/roleViewPreference"
import { resolveActiveShopId } from "@/utils/shopScope"
import { markObservedAuthToken, markObservedScopeToken } from "@/utils/tokenScopeSession"
import { usePermissionStore } from "./permission"
import { useSettingsStore } from "./settings"
import {
  createShiftStatusSnapshot,
  createUnknownShiftStatusSnapshot,
  resolveShiftStatusForShop,
} from "./shiftStatus"
import { useTagsViewStore } from "./tags-view"
import { hasUsableRolePermissionIds, resolveActiveRolePermissionList } from "./userRolePermissions"
import { useViewScopeStoreHook } from "./viewScope"
import { useWsStore } from "./wsStore"

export type RoleViewValue = PersistedRoleViewValue

export interface RoleViewOption {
  label: string
  value: RoleViewValue
  description?: string
}

export interface UserPrintSetting {
  printWidth: string
  receiptPrintTargetType?: ReceiptPrintTargetType
  receiptPrinterRelId?: number
  checkoutReceiptDefaultChecked?: boolean
  checkoutReceiptFooter?: CheckoutReceiptFooterConfig
  localPrinterStyle?: Partial<LocalPrinterStyleConfig>
  localPrinterProfiles?: Record<string, {
    printWidth?: string
    style?: Partial<LocalPrinterStyleConfig>
  }>
}

type LegacyUserPrintSetting = Partial<UserPrintSetting> & {
  enableItemOrderLocalPrint?: number
}

interface GetInfoOptions extends CallerHandledAuthFailureOptions {
  skipShopBootstrap?: boolean
  includeShopBootstrap?: boolean
  businessContextSystemVersion?: SystemVersionDTO
  isBusinessContextCurrent?: () => boolean
  businessContextScope?: OrganizationScope
  businessContextScopeId?: string
}

interface AuthSessionRequestContext {
  generation: number
  token: string
  infoRequestId?: number
}

interface ScopeTokenSwitchOptions extends CallerHandledAuthFailureOptions {
  restorePreferredRole?: boolean
}

function createAuthSessionChangedError() {
  const error = new Error("登录上下文已变化")
  error.name = "AuthSessionChangedError"
  return error
}

const ROLE_VIEW_ALL: RoleViewValue = "all"
const HQ_ENTRY_ROLE_NAME = "连锁总部入口"
const OAUTH_CLIENT_STORAGE_KEY = "teashop-oauth-client"

const resolvePositiveScopeIdPart = (scopeId: string, prefix: string) => {
  if (!scopeId.startsWith(prefix)) return undefined
  const value = Number(scopeId.slice(prefix.length))
  return Number.isSafeInteger(value) && value > 0 ? value : undefined
}

const assertLegacyScopeMatchesRoleInfo = (
  scope: OrganizationScope | undefined,
  scopeId: string,
  merchantUser: MerchantUserType,
) => {
  const isShopScope = scope?.type === "shop"
    || scope?.type === "chain-store"
    || scopeId.startsWith("shop-")
    || scopeId.startsWith("chain-store-")
  if (isShopScope) {
    const expectedShopId = Number(scope?.meta?.shopId)
      || resolvePositiveScopeIdPart(scopeId, "shop-")
      || resolvePositiveScopeIdPart(scopeId, "chain-store-")
    if (!expectedShopId || Number(merchantUser.shopId) !== expectedShopId) {
      throw new Error("机构切换返回的门店范围不一致")
    }
    return
  }

  const expectedBrandId = Number(scope?.meta?.brandId)
    || resolvePositiveScopeIdPart(scopeId, "chain-headquarter-")
  const actualBrandId = Number(merchantUser.brandId)
  if (
    expectedBrandId
    && Number.isSafeInteger(actualBrandId)
    && actualBrandId > 0
    && actualBrandId !== expectedBrandId
  ) {
    throw new Error("机构切换返回的品牌范围不一致")
  }
}

const resolveScopeAccountId = (id: unknown, phone?: unknown) => {
  if (id !== undefined && id !== null && `${id}`.trim()) {
    return `corp-user:${String(id).trim()}`
  }
  if (typeof phone === "string" && phone.trim()) {
    return `phone:${phone.trim()}`
  }
  return ""
}

const readOAuthClient = (): OAuthClient => {
  return localStorage.getItem(OAUTH_CLIENT_STORAGE_KEY) === "teashop_merchant" ? "teashop_merchant" : "teashop"
}

const normalizeUserPrintSetting = (setting?: LegacyUserPrintSetting | null, defaultPrintWidth = "58mm"): UserPrintSetting => {
  const { enableItemOrderLocalPrint: _legacy, ...normalizedSetting } = setting ?? {}
  return {
    ...normalizedSetting,
    printWidth: normalizedSetting.printWidth || defaultPrintWidth,
    checkoutReceiptDefaultChecked: normalizedSetting.checkoutReceiptDefaultChecked === true,
    checkoutReceiptFooter: resolveCheckoutReceiptFooterConfig(normalizedSetting.checkoutReceiptFooter),
    ...normalizeReceiptPrintTarget(normalizedSetting),
  }
}

const isScopeEntryRole = (role: RoleType) => {
  return role.roleName === HQ_ENTRY_ROLE_NAME
}

function resolveRoleViewOptionsForState(options: {
  roles: RoleType[]
  businessRoles: RoleCreateType[]
  roleViewAvailable: boolean
  isHeadquarter: boolean
}): RoleViewOption[] {
  const visibleRoles = options.roles.filter((role) => {
    return !isScopeEntryRole(role)
      && (
        options.isHeadquarter
        || options.businessRoles.length === 0
        || hasUsableRolePermissionIds(options.businessRoles, `${role.merchantRoleId}`)
      )
  })
  if (
    !options.roleViewAvailable
    || (options.isHeadquarter ? visibleRoles.length === 0 : options.roles.length <= 1)
  ) {
    return []
  }
  return [
    { label: options.isHeadquarter ? "全部总部角色" : "全部岗位", value: ROLE_VIEW_ALL },
    ...visibleRoles.map(role => ({
      label: role.roleName,
      value: `${role.merchantRoleId}` as RoleViewValue,
      description: role.description,
    })),
  ]
}

export const useUserStore = defineStore("user", () => {
  let authSessionGeneration = 0
  let latestInfoRequestId = 0
  let latestAuthMutationId = 0
  const token = ref<string>(getToken() || "")
  const refreshToken = ref<string>(getRefreshToken() || "")
  const oauthClient = ref<OAuthClient>(readOAuthClient())
  const roles = ref<RoleType[]>([])
  const businessRoles = ref<RoleCreateType[]>([])
  const allPermissions = ref<PermissionType[]>([])
  const activeRoleView = ref<RoleViewValue>(ROLE_VIEW_ALL)
  const roleViewAvailable = ref(true)
  const username = ref<string>("")
  const userLoginInfo = ref<LoginUserInfo>()
  const shopId = ref<number>(0)
  const userInfo = ref<MerchantUserType>()
  const shopInfo = ref<ShopInfoType>({} as ShopInfoType)
  const permissionList = ref<string[]>([])
  const organizationSelectionRequired = ref(false)
  const permissionStore = usePermissionStore()
  const tagsViewStore = useTagsViewStore()
  const HandoverDetail = ref()
  const shiftStatusSnapshot = ref(createUnknownShiftStatusSnapshot())
  const resetShiftStatus = () => {
    shiftStatusSnapshot.value = createUnknownShiftStatusSnapshot()
  }
  const setShiftStatus = (status: ShiftStatus, targetShopId = shopId.value) => {
    if (status === "unknown" || !Number.isFinite(targetShopId) || targetShopId <= 0) {
      resetShiftStatus()
      return
    }
    shiftStatusSnapshot.value = createShiftStatusSnapshot(targetShopId, status)
  }
  const getShiftStatusForShop = (targetShopId = shopId.value) => {
    return resolveShiftStatusForShop(shiftStatusSnapshot.value, targetShopId)
  }
  const isOpen = computed(() => getShiftStatusForShop() === "open")
  const shopName = ref()
  const printSetting = ref<UserPrintSetting>(normalizeUserPrintSetting())
  let issuedRoleView: {
    scopeId: string
    value: RoleViewValue
    expectedCorpUserId?: number
    expectedMerchantUserIds?: number[]
    usesLegacyTicket: boolean
  } | undefined
  let refreshSessionRequest: Promise<boolean> | undefined

  const isHeadquarterScope = (
    scope?: OrganizationScope,
    scopeId?: string,
  ) => {
    const viewScopeStore = useViewScopeStoreHook()
    return isHqScope(
      scope ?? viewScopeStore.activeScope,
      scopeId ?? viewScopeStore.activeScopeId,
    )
  }

  const isHeadquarterScopeActive = () => isHeadquarterScope()

  const getActiveRoleViewScopeId = () => {
    return useViewScopeStoreHook().activeScopeId
  }

  const invalidateAuthSession = () => {
    authSessionGeneration += 1
    latestInfoRequestId += 1
    latestAuthMutationId += 1
  }

  const beginAuthMutation = () => {
    latestAuthMutationId += 1
    return latestAuthMutationId
  }

  const assertAuthMutationCurrent = (mutationId: number) => {
    if (mutationId !== latestAuthMutationId) {
      throw createAuthSessionChangedError()
    }
  }

  const captureAuthSessionContext = (infoRequestId?: number): AuthSessionRequestContext => ({
    generation: authSessionGeneration,
    token: getToken() ?? "",
    ...(infoRequestId === undefined ? {} : { infoRequestId }),
  })

  const assertAuthSessionCurrent = (context: AuthSessionRequestContext) => {
    if (
      context.generation !== authSessionGeneration
      || context.token !== (getToken() ?? "")
      || (context.infoRequestId !== undefined && context.infoRequestId !== latestInfoRequestId)
    ) {
      throw createAuthSessionChangedError()
    }
  }

  const assertBusinessContextCurrent = (guard?: () => boolean) => {
    if (guard && !guard()) {
      throw createAuthSessionChangedError()
    }
  }

  const resolveRoleViewOptions = (isHeadquarter = isHeadquarterScopeActive()): RoleViewOption[] => {
    return resolveRoleViewOptionsForState({
      roles: roles.value,
      businessRoles: businessRoles.value,
      roleViewAvailable: roleViewAvailable.value,
      isHeadquarter,
    })
  }

  const roleViewOptions = computed<RoleViewOption[]>(() => resolveRoleViewOptions())

  const resolveActiveRolePermissionListForScope = (
    scope?: OrganizationScope,
    scopeId?: string,
  ) => {
    if (isHeadquarterScope(scope, scopeId)) {
      // 总部 role_info 已由当前 scope token 收窄，不能再套用门店角色权限映射。
      return permissionList.value
    }
    return resolveActiveRolePermissionList({
      activeRoleView: activeRoleView.value,
      allRoleView: ROLE_VIEW_ALL,
      permissionList: permissionList.value,
      businessRoles: businessRoles.value,
      allPermissions: allPermissions.value,
    })
  }

  const activeRolePermissionList = computed(() => resolveActiveRolePermissionListForScope())

  const activeRoleList = computed(() => {
    if (activeRoleView.value === ROLE_VIEW_ALL) {
      return roles.value
    }
    return roles.value.filter((role) => `${role.merchantRoleId}` === activeRoleView.value)
  })

  const activeRoleProfile = computed(() => {
    return resolveRoleProfile(activeRolePermissionList.value, activeRoleList.value)
  })

  const activeRoleDefaultHomePath = computed(() => {
    return resolveRoleDefaultHomePath(activeRoleProfile.value, activeRolePermissionList.value)
  })

  const getRoleViewCorpUserId = () => {
    const value = Number(userInfo.value?.corpUserId ?? userLoginInfo.value?.user_info?.id)
    return Number.isSafeInteger(value) && value > 0 ? value : undefined
  }

  const readPreferredRoleView = (scopeId = useViewScopeStoreHook().activeScopeId) => {
    return readRoleViewPreference(scopeId, getRoleViewCorpUserId())?.roleView ?? ROLE_VIEW_ALL
  }

  const stageActiveRoleView = (
    value: RoleViewValue,
    scope?: OrganizationScope,
    scopeId?: string,
  ) => {
    const isHeadquarter = isHeadquarterScope(scope, scopeId)
    if (!isHeadquarter && roles.value.length <= 1) {
      const onlyRole = roles.value[0]?.merchantRoleId
        ? `${roles.value[0].merchantRoleId}` as RoleViewValue
        : ROLE_VIEW_ALL
      activeRoleView.value = onlyRole === ROLE_VIEW_ALL || hasUsableRolePermissionIds(businessRoles.value, onlyRole)
        ? onlyRole
        : ROLE_VIEW_ALL
      return value === ROLE_VIEW_ALL || value === activeRoleView.value
    }
    const allowedValues = resolveRoleViewOptions(isHeadquarter).map(option => option.value)
    activeRoleView.value = allowedValues.includes(value) ? value : ROLE_VIEW_ALL
    return activeRoleView.value === value
  }

  const setActiveRoleView = (value: RoleViewValue) => {
    const accepted = stageActiveRoleView(value)
    const scopeId = getActiveRoleViewScopeId()
    const corpUserId = userInfo.value?.corpUserId
    if (accepted) {
      if (scopeId && corpUserId) {
        return saveRoleViewPreference(scopeId, corpUserId, value)
      }
      return false
    }
    clearRoleViewPreference(scopeId, corpUserId)
    activeRoleView.value = ROLE_VIEW_ALL
    return false
  }

  const resetRoleViewState = () => {
    permissionList.value = []
    businessRoles.value = []
    allPermissions.value = []
    activeRoleView.value = ROLE_VIEW_ALL
    roleViewAvailable.value = true
  }

  const resetBusinessIdentityState = () => {
    resetShiftStatus()
    useSettingsStore().clearBusinessDate()
    shopId.value = 0
    userInfo.value = undefined
    shopInfo.value = {} as ShopInfoType
    HandoverDetail.value = undefined
    shopName.value = undefined
    printSetting.value = normalizeUserPrintSetting()
  }

  const clearScopedPermissionState = () => {
    invalidateAuthSession()
    issuedRoleView = undefined
    resetRoleViewState()
    roles.value = []
    resetBusinessIdentityState()
    permissionStore.setRoutes([], [])
    resetRouter()
    _resetTagsView()
  }

  const resetLocalAccountSession = () => {
    issuedRoleView = undefined
    resetRoleViewState()
    roles.value = []
    resetBusinessIdentityState()
    userLoginInfo.value = undefined
    username.value = ""
    organizationSelectionRequired.value = false
    permissionStore.setRoutes([], [])
    useWsStore().disconnect()
    resetRouter()
    _resetTagsView()
  }

  const markOrganizationSelectionRequired = () => {
    organizationSelectionRequired.value = true
  }

  const clearOrganizationSelectionRequired = () => {
    organizationSelectionRequired.value = false
  }

  const setOAuthClient = (client: OAuthClient) => {
    oauthClient.value = client
    localStorage.setItem(OAUTH_CLIENT_STORAGE_KEY, client)
  }

  const applyLoginSession = (
    res: LoginUserInfo,
    options: { requireOrganizationSelection?: boolean, oauthClient?: OAuthClient } = {},
  ) => {
    invalidateAuthSession()
    if (options.requireOrganizationSelection) {
      // A fresh account login is an identity boundary, not another scoped-token
      // refresh. Remove every in-memory principal from the previous account before
      // preferences or scope tickets are resolved for the new one.
      resetLocalAccountSession()
      useViewScopeStoreHook().bindAccount(resolveScopeAccountId(res.user_info.id, res.user_info.phone))
    }
    resetShiftStatus()
    userLoginInfo.value = res
    username.value = res.user_info.name
    setToken(res.access_token)
    setRefreshToken(res.refresh_token)
    setOAuthClient(options.oauthClient ?? oauthClient.value)
    markObservedAuthToken(res.access_token)
    token.value = res.access_token
    refreshToken.value = res.refresh_token
    if (options.requireOrganizationSelection) {
      markOrganizationSelectionRequired()
    } else {
      clearOrganizationSelectionRequired()
    }
  }

  const loadViewScopeOrganizationsForPermissionSession = async (
    options: CallerHandledAuthFailureOptions = {},
  ) => {
    return fetchOrganizationsForCurrentSession({
      callerHandlesAuthFailure: true,
      ...options,
    })
  }

  const createScopeSwitchRollback = () => {
    const roleViewScopeId = getActiveRoleViewScopeId()
    const roleViewCorpUserId = userInfo.value?.corpUserId
    const snapshot = {
      tokenCookie: getToken() ?? "",
      refreshTokenCookie: getRefreshToken() ?? "",
      token: token.value,
      refreshToken: refreshToken.value,
      oauthClient: oauthClient.value,
      roles: roles.value,
      businessRoles: businessRoles.value,
      allPermissions: allPermissions.value,
      activeRoleView: activeRoleView.value,
      roleViewAvailable: roleViewAvailable.value,
      username: username.value,
      userLoginInfo: userLoginInfo.value,
      shopId: shopId.value,
      userInfo: userInfo.value,
      shopInfo: shopInfo.value,
      permissionList: permissionList.value,
      organizationSelectionRequired: organizationSelectionRequired.value,
      HandoverDetail: HandoverDetail.value,
      shiftStatusSnapshot: shiftStatusSnapshot.value,
      shopName: shopName.value,
      printSetting: printSetting.value,
      businessDate: useSettingsStore().getBusinessDateMinutes(),
      issuedRoleView,
      roleViewScopeId,
      roleViewCorpUserId,
      roleViewPreference: readRoleViewPreference(roleViewScopeId, roleViewCorpUserId),
    }

    return (expectedToken?: string) => {
      if (expectedToken && (getToken() ?? "") !== expectedToken) {
        return false
      }
      invalidateAuthSession()
      if (snapshot.tokenCookie) setToken(snapshot.tokenCookie)
      else removeToken()
      if (snapshot.refreshTokenCookie) setRefreshToken(snapshot.refreshTokenCookie)
      else removeRefreshToken()
      token.value = snapshot.token
      refreshToken.value = snapshot.refreshToken
      setOAuthClient(snapshot.oauthClient)
      roles.value = snapshot.roles
      businessRoles.value = snapshot.businessRoles
      allPermissions.value = snapshot.allPermissions
      activeRoleView.value = snapshot.activeRoleView
      roleViewAvailable.value = snapshot.roleViewAvailable
      username.value = snapshot.username
      userLoginInfo.value = snapshot.userLoginInfo
      shopId.value = snapshot.shopId
      useSettingsStore().clearBusinessDate()
      if (snapshot.businessDate !== undefined) {
        useSettingsStore().setBussinessDate(snapshot.businessDate, snapshot.shopId)
      }
      userInfo.value = snapshot.userInfo
      shopInfo.value = snapshot.shopInfo
      permissionList.value = snapshot.permissionList
      organizationSelectionRequired.value = snapshot.organizationSelectionRequired
      HandoverDetail.value = snapshot.HandoverDetail
      shiftStatusSnapshot.value = snapshot.shiftStatusSnapshot
      shopName.value = snapshot.shopName
      printSetting.value = snapshot.printSetting
      issuedRoleView = snapshot.issuedRoleView
      if (snapshot.roleViewPreference) {
        saveRoleViewPreference(
          snapshot.roleViewPreference.scopeId,
          snapshot.roleViewPreference.corpUserId,
          snapshot.roleViewPreference.roleView,
        )
      } else {
        clearRoleViewPreference(snapshot.roleViewScopeId, snapshot.roleViewCorpUserId)
      }
      markObservedAuthToken(snapshot.tokenCookie)
      return true
    }
  }

  const createRoleViewPreferenceRollback = (scopeId: string) => {
    const corpUserId = getRoleViewCorpUserId()
    const snapshot = readRoleViewPreference(scopeId, corpUserId)
    return () => {
      if (snapshot) {
        return saveRoleViewPreference(
          snapshot.scopeId,
          snapshot.corpUserId,
          snapshot.roleView,
        )
      }
      clearRoleViewPreference(scopeId, corpUserId)
      return true
    }
  }

  const refreshPermissionSessionFromCurrentState = (
    reason: string,
    requestOptions: CallerHandledAuthFailureOptions = {},
    transaction?: BusinessContextTransaction,
  ) => {
    const viewScopeStore = useViewScopeStoreHook()
    return refreshPermissionSession(reason, {
      getToken,
      router,
      resetRouter,
      loadOrganizations: internalRequestOptions =>
        loadViewScopeOrganizationsForPermissionSession({
          ...requestOptions,
          ...internalRequestOptions,
        }),
      viewScopeStore,
      userStore: {
        prepareBusinessContext,
        getInfo,
        fetchShopInfo,
        getActiveRoleList: () => activeRoleList.value,
        getActivePermissionList: resolveActiveRolePermissionListForScope,
        getActiveRoleView: () => activeRoleView.value,
        getDefaultHomePath: () => activeRoleDefaultHomePath.value,
        getPreferredRoleView: readPreferredRoleView,
        stageActiveRoleView,
        setActiveRoleView,
        syncBusinessWebSocket,
        disconnectBusinessWebSocket,
        createScopeSwitchRollback,
        getBusinessContextAccountId: () =>
          resolveScopeAccountId(userInfo.value?.corpUserId, userInfo.value?.phone),
      },
      permissionStore,
      tagsViewStore,
    }, {
      getInfoOptions: requestOptions,
    }, transaction)
  }

  /** 设置角色数组 */
  const setRoles = (value: RoleType[]) => {
    roles.value = value
  }

  const setBusinessRoles = (value: RoleCreateType[]) => {
    businessRoles.value = value
  }

  /** 登录 */
  const login = (loginData: ILoginRequestData) => {
    return new Promise((resolve, reject) => {
      const authMutationId = beginAuthMutation()
      const code = `PHONE@${loginData.phone}@${loginData.code}@merchant`
      loginApi(code)
        .then((res) => {
          assertAuthMutationCurrent(authMutationId)
          applyLoginSession(res, { requireOrganizationSelection: true, oauthClient: "teashop" })
          resolve(true)
        })
        .catch((error) => {
          reject(error)
        })
    })
  }

  /** 微信登录 */
  const wxLogin = (code: string) => {
    return new Promise((resolve, reject) => {
      const authMutationId = beginAuthMutation()
      wxLoginApi(code)
        .then((res) => {
          assertAuthMutationCurrent(authMutationId)
          applyLoginSession(res, { requireOrganizationSelection: true, oauthClient: "teashop" })
          resolve(true)
        })
        .catch((error) => {
          reject(error)
        })
    })
  }

  /** 刷新 token */
  const refreshTokenRequest = () => {
    if (refreshSessionRequest) {
      recordBusinessContextTrace("auth-refresh.reused-inflight")
      return refreshSessionRequest
    }

    const request = coordinateBusinessContextRefresh(async (transaction) => {
      const authMutationId = beginAuthMutation()
      let refreshedSessionApplied = false
      const scope = userLoginInfo.value?.scope || "server"
      const refreshClient = readOAuthClient()
      const latestRefreshToken = getRefreshToken() || refreshToken.value
      const viewScopeStore = useViewScopeStoreHook()
      const committedScopeId = viewScopeStore.activeScopeId
      const committedRoleView = activeRoleView.value
      recordBusinessContextTrace("auth-refresh.started", {
        committedScopeId,
        committedRoleView,
        activeScopeId: viewScopeStore.activeScopeId,
        oauthClient: refreshClient,
      })
      setOAuthClient(refreshClient)
      refreshToken.value = latestRefreshToken
      try {
        const res = await refreshTokenApi(scope, latestRefreshToken, { client: refreshClient })
        assertAuthMutationCurrent(authMutationId)
        transaction.assertCurrent()
        applyLoginSession(res, { oauthClient: refreshClient })
        refreshedSessionApplied = true
        recordBusinessContextTrace("auth-refresh.account-session-applied", {
          committedScopeId,
          activeScopeId: viewScopeStore.activeScopeId,
        })
        if (committedScopeId) {
          if (viewScopeStore.activeScopeId !== committedScopeId) {
            throw createAuthSessionChangedError()
          }
          // OAuth refresh only proves the account session. It does not prove
          // that the refreshed token still carries the selected organization.
          // Reissue the committed scope before any permission or route state is
          // hydrated, otherwise a default HQ token can be mislabeled as a shop
          // token and send the user back to headquarters.
          await switchScopeToken(
            committedScopeId,
            committedRoleView,
            {
              callerHandlesAuthFailure: true,
              restorePreferredRole: true,
              skipBusinessContextBarrier: true,
            },
          )
          recordBusinessContextTrace("auth-refresh.scope-rebound", {
            committedScopeId,
            activeScopeId: viewScopeStore.activeScopeId,
            committedRoleView,
          })
          transaction.assertCurrent()
          if (viewScopeStore.activeScopeId !== committedScopeId) {
            throw createAuthSessionChangedError()
          }
        }
        disconnectBusinessWebSocket()
        const result = await refreshPermissionSessionFromCurrentState(
          "refresh-token",
          { skipBusinessContextBarrier: true },
          transaction,
        )
        if (!result.applied) {
          throw new Error(`刷新 token 后权限状态同步失败:${result.discardReason}`)
        }
        recordBusinessContextTrace("auth-refresh.permissions-applied", {
          committedScopeId,
          activeScopeId: viewScopeStore.activeScopeId,
        })
        return true
      } catch (error) {
        recordBusinessContextTrace("auth-refresh.failed", {
          committedScopeId,
          activeScopeId: viewScopeStore.activeScopeId,
          refreshedSessionApplied,
          error,
        })
        if (refreshedSessionApplied) {
          invalidateBusinessContextRuntime({
            userStore: {
              clearScopedPermissionState,
              disconnectBusinessWebSocket,
            },
            viewScopeStore: useViewScopeStoreHook(),
            invalidateSystemVersionRuntime,
          }, { requestRecovery: true })
        }
        throw error
      }
    })
    refreshSessionRequest = request
    request.then(
      () => {
        if (refreshSessionRequest === request) refreshSessionRequest = undefined
      },
      () => {
        if (refreshSessionRequest === request) refreshSessionRequest = undefined
      },
    )
    return request
  }

  const hasShopBootstrapContext = (
    scope?: OrganizationScope,
    scopeId?: string,
  ) => {
    if (
      scope?.type === "chain-headquarter"
      || scope?.type === "chain-region"
      || scopeId?.startsWith("chain-headquarter-")
      || scopeId?.startsWith("chain-region-")
    ) {
      return false
    }
    const activeScope = scope ?? useViewScopeStoreHook().activeScope
    return resolveActiveShopId({
      activeScope,
      userShopId: shopId.value,
    }) === shopId.value
  }

  const loadBusinessDate = async (
    targetShopId: number,
    businessContextSystemVersion?: SystemVersionDTO,
    skipBusinessContextBarrier?: boolean,
  ) => {
    const res = await getNowBussinessDate({
      callerHandlesAuthFailure: true,
      businessContextSystemVersion,
      skipBusinessContextBarrier,
    })
    if (res.code !== 0 || !res.data || res.data.shopId !== targetShopId) {
      throw new Error("营业日配置读取失败，请重新进入门店")
    }
    // Historical null values use midnight in the backend; failed reads do not.
    const minute = res.data.businessStartTime ?? 0
    if (!Number.isInteger(minute) || minute < 0 || minute >= 24 * 60) {
      throw new Error("营业日配置无效，请检查门店设置")
    }
    return minute
  }

  const syncBusinessWebSocket = () => {
    const wsStore = useWsStore()
    try {
      if (hasShopBootstrapContext()) {
        wsStore.connect(shopId.value)
        wsStore.send("ping")
      } else {
        wsStore.disconnect()
      }
    } catch (error) {
      console.warn("业务 WebSocket 同步失败:", error)
    }
  }

  const disconnectBusinessWebSocket = () => {
    useWsStore().disconnect()
  }

  /** 获取店铺详情 */
  const fetchShopInfo = async (
    businessContextSystemVersion?: SystemVersionDTO,
    guard: {
      isCurrent?: () => boolean
      scope?: OrganizationScope
      scopeId?: string
      skipBusinessContextBarrier?: boolean
    } = {},
  ) => {
    if (!hasShopBootstrapContext(guard.scope, guard.scopeId)) {
      // Aggregate HQ/region tokens intentionally do not carry a current shop.
      return
    }

    const targetShopId = shopId.value
    const requestContext = captureAuthSessionContext()
    const [res, businessDate] = await Promise.all([
      getShopInfoApi(targetShopId, {
        callerHandlesAuthFailure: true,
        businessContextSystemVersion,
        skipBusinessContextBarrier: guard.skipBusinessContextBarrier,
      }),
      loadBusinessDate(targetShopId, businessContextSystemVersion, guard.skipBusinessContextBarrier),
    ])
    assertAuthSessionCurrent(requestContext)
    assertBusinessContextCurrent(guard.isCurrent)
    if (shopId.value === targetShopId) {
      const data = res.data
      shopInfo.value = data
      shopName.value = data.shopName
      if (shopInfo.value.businessStatus != 2) {
        ElMessage({
          type: "error",
          message: shopInfo.value.businessStatus == 5 ? "当前店铺已过期" : "当前店铺未开通后台",
        })
        logout()
      }
      printSetting.value = data.printSetting
        ? normalizeUserPrintSetting(JSON.parse(data.printSetting))
        : normalizeUserPrintSetting(null, "80mm")
      useSettingsStore().setBussinessDate(businessDate, targetShopId)
    }
    return res
  }

  const loadUserBusinessInfo = async (
    roles: RoleType[],
    targetShopId: number,
    requestContext = captureAuthSessionContext(),
    businessContextSystemVersion?: SystemVersionDTO,
    requestOptions: CallerHandledAuthFailureOptions = {},
  ) => {
    assertAuthSessionCurrent(requestContext)
    try {
      const [roleRes, permissionRes] = await Promise.all([
        getRoleDetailListByShopId(targetShopId, {
          callerHandlesAuthFailure: requestOptions.callerHandlesAuthFailure,
          businessContextSystemVersion,
          skipBusinessContextBarrier: requestOptions.skipBusinessContextBarrier,
        }),
        getPermissionListAll({
          callerHandlesAuthFailure: requestOptions.callerHandlesAuthFailure,
          businessContextSystemVersion,
          skipBusinessContextBarrier: requestOptions.skipBusinessContextBarrier,
        }),
      ])
      assertAuthSessionCurrent(requestContext)
      const currentRoles = roleRes.data.filter((item) => {
        return roles.some(role => role.merchantRoleId === item.merchantRole.merchantRoleId)
      })
      return {
        roles: currentRoles,
        permissions: permissionRes.data,
        available: true,
      }
    } catch (_error: unknown) {
      assertAuthSessionCurrent(requestContext)
      return {
        roles: [] as RoleCreateType[],
        permissions: [] as PermissionType[],
        available: false,
      }
    }
  }

  /**
   * Loads a complete candidate without publishing it to Pinia. The coordinator
   * owns the only commit point, after version/scope/permission validation.
   */
  const prepareBusinessContext = async (options: GetInfoOptions = {}) => {
    const infoRequestId = ++latestInfoRequestId
    const requestContext = captureAuthSessionContext(infoRequestId)
    const res = await getUserRolesApi(options)
    assertAuthSessionCurrent(requestContext)
    assertBusinessContextCurrent(options.isBusinessContextCurrent)

    const data = res.data
    const viewScopeStore = useViewScopeStoreHook()
    const targetScopeId = options.businessContextScopeId ?? viewScopeStore.activeScopeId
    const targetScope = options.businessContextScope
      ?? viewScopeStore.organizations.find(item => item.id === targetScopeId)
      ?? viewScopeStore.activeScope
    const isHeadquarter = isHeadquarterScope(targetScope, targetScopeId)
    const issuedContext = issuedRoleView?.scopeId === targetScopeId
      ? issuedRoleView
      : undefined
    if (
      issuedContext?.expectedMerchantUserIds
      && !issuedContext.expectedMerchantUserIds.includes(Number(data.merchantUser.merchantUserId))
    ) {
      throw new Error("机构切换返回的用户身份不一致")
    }
    if (issuedContext?.usesLegacyTicket) {
      assertLegacyScopeMatchesRoleInfo(targetScope, targetScopeId, data.merchantUser)
    }

    const nextShopId = resolveActiveShopId({
      activeScope: targetScope,
      userShopId: data.merchantUser.shopId,
    }) ?? 0
    const canBootstrapShop = !options.skipShopBootstrap
      && nextShopId > 0
    const businessInfo = canBootstrapShop
      ? await loadUserBusinessInfo(
        data.merchantRoleList,
        nextShopId,
        requestContext,
        options.businessContextSystemVersion,
        options,
      )
      : undefined
    assertAuthSessionCurrent(requestContext)
    assertBusinessContextCurrent(options.isBusinessContextCurrent)

    const candidateRoles = data.merchantRoleList?.length > 0
      ? data.merchantRoleList
      : asyncRouteSettings.defaultRoles
    const candidateBusinessRoles = businessInfo?.roles ?? []
    const candidateAllPermissions = businessInfo?.permissions ?? []
    const candidateRoleViewAvailable = businessInfo?.available ?? isHeadquarter
    let candidateActiveRoleView: RoleViewValue = ROLE_VIEW_ALL
    const stageCandidateRoleView = (value: RoleViewValue) => {
      if (!isHeadquarter && candidateRoles.length <= 1) {
        const onlyRole = candidateRoles[0]?.merchantRoleId
          ? `${candidateRoles[0].merchantRoleId}` as RoleViewValue
          : ROLE_VIEW_ALL
        candidateActiveRoleView = onlyRole === ROLE_VIEW_ALL
          || hasUsableRolePermissionIds(candidateBusinessRoles, onlyRole)
          ? onlyRole
          : ROLE_VIEW_ALL
        return value === ROLE_VIEW_ALL || value === candidateActiveRoleView
      }
      const allowedValues = resolveRoleViewOptionsForState({
        roles: candidateRoles,
        businessRoles: candidateBusinessRoles,
        roleViewAvailable: candidateRoleViewAvailable,
        isHeadquarter,
      }).map(option => option.value)
      candidateActiveRoleView = allowedValues.includes(value) ? value : ROLE_VIEW_ALL
      return candidateActiveRoleView === value
    }

    const serverIssuedRoleView = issuedContext?.value
    if (serverIssuedRoleView) {
      const expectedCorpUserId = issuedContext?.expectedCorpUserId
      const corpUserMatches = expectedCorpUserId === undefined
        || expectedCorpUserId === data.merchantUser.corpUserId
      if (!corpUserMatches) {
        throw new Error("机构切换返回的用户身份不一致")
      }
      if (!stageCandidateRoleView(serverIssuedRoleView)) {
        throw new Error(
          isHeadquarter
            ? "总部角色偏好与当前授权不一致，请重新选择机构"
            : "分店角色与当前授权不一致，请重新选择机构",
        )
      }
    } else if (!isHeadquarter && candidateRoles.length <= 1) {
      stageCandidateRoleView(ROLE_VIEW_ALL)
    } else {
      const preference = readRoleViewPreference(targetScopeId, data.merchantUser.corpUserId)
      if (preference && !stageCandidateRoleView(preference.roleView)) {
        if (isHeadquarter) {
          throw new Error("已保存的总部角色不再可用，请重新选择机构")
        }
      }
    }

    let candidateShopInfo: ShopInfoType | undefined
    let candidatePrintSetting: UserPrintSetting | undefined
    let candidateBusinessDate: number | undefined
    if (canBootstrapShop && options.includeShopBootstrap) {
      // Finish all bootstrap reads before commit advances the context revision.
      const [shopResponse, businessDate] = await Promise.all([
        getShopInfoApi(nextShopId, {
          callerHandlesAuthFailure: options.callerHandlesAuthFailure,
          businessContextSystemVersion: options.businessContextSystemVersion,
          skipBusinessContextBarrier: options.skipBusinessContextBarrier,
        }),
        loadBusinessDate(nextShopId, options.businessContextSystemVersion, options.skipBusinessContextBarrier),
      ])
      assertAuthSessionCurrent(requestContext)
      assertBusinessContextCurrent(options.isBusinessContextCurrent)
      candidateBusinessDate = businessDate
      candidateShopInfo = shopResponse.data
      candidatePrintSetting = candidateShopInfo.printSetting
        ? normalizeUserPrintSetting(JSON.parse(candidateShopInfo.printSetting))
        : normalizeUserPrintSetting(null, "80mm")
      if (candidateShopInfo.businessStatus != 2) {
        const message = candidateShopInfo.businessStatus == 5
          ? "当前店铺已过期"
          : "当前店铺未开通后台"
        ElMessage({ type: "error", message })
        throw new Error(message)
      }
    }

    const getCandidatePermissionList = () => {
      if (isHeadquarter) return data.permissionList
      return resolveActiveRolePermissionList({
        activeRoleView: candidateActiveRoleView,
        allRoleView: ROLE_VIEW_ALL,
        permissionList: data.permissionList,
        businessRoles: candidateBusinessRoles,
        allPermissions: candidateAllPermissions,
      })
    }
    const getCandidateRoleList = () => {
      if (candidateActiveRoleView === ROLE_VIEW_ALL) return candidateRoles
      return candidateRoles.filter(
        role => `${role.merchantRoleId}` === candidateActiveRoleView,
      )
    }

    return {
      response: res,
      shopBootstrapComplete: !canBootstrapShop || options.includeShopBootstrap === true,
      stageActiveRoleView: stageCandidateRoleView,
      getActivePermissionList: getCandidatePermissionList,
      getActiveRoleList: getCandidateRoleList,
      getActiveRoleView: () => candidateActiveRoleView,
      getDefaultHomePath: () => {
        const permissions = getCandidatePermissionList()
        return resolveRoleDefaultHomePath(
          resolveRoleProfile(permissions, getCandidateRoleList()),
          permissions,
        )
      },
      getBusinessContextAccountId: () =>
        resolveScopeAccountId(data.merchantUser.corpUserId, data.merchantUser.phone),
      commit: () => {
        assertAuthSessionCurrent(requestContext)
        assertBusinessContextCurrent(options.isBusinessContextCurrent)
        if (shopId.value !== nextShopId) {
          resetShiftStatus()
          useSettingsStore().clearBusinessDate()
        }
        shopId.value = nextShopId
        username.value = data.merchantUser.name
        roles.value = candidateRoles
        userInfo.value = data.merchantUser
        permissionList.value = data.permissionList
        businessRoles.value = candidateBusinessRoles
        allPermissions.value = candidateAllPermissions
        roleViewAvailable.value = candidateRoleViewAvailable
        activeRoleView.value = candidateActiveRoleView
        if (candidateShopInfo) {
          shopInfo.value = candidateShopInfo
          shopName.value = candidateShopInfo.shopName
          printSetting.value = candidatePrintSetting!
          useSettingsStore().setBussinessDate(candidateBusinessDate!, nextShopId)
        } else if (nextShopId === 0) {
          shopInfo.value = {} as ShopInfoType
          shopName.value = undefined
          printSetting.value = normalizeUserPrintSetting()
        }
        if (issuedContext && issuedRoleView === issuedContext) {
          issuedRoleView = undefined
        }
      },
    }
  }

  /** Compatibility entry point; orchestration uses prepareBusinessContext directly. */
  const getInfo = async (_ws: boolean = false, options: GetInfoOptions = {}) => {
    try {
      const prepared = await prepareBusinessContext(options)
      prepared.commit()
      return prepared.response
    } catch (error) {
      if (
        error instanceof Error
        && (
          error.message.includes("总部角色偏好与当前授权不一致")
          || error.message.includes("分店角色与当前授权不一致")
          || error.message.includes("已保存的总部角色不再可用")
        )
      ) {
        clearScopedPermissionState()
      }
      throw error
    }
  }

  const switchScopeToken = async (
    scopeId: string,
    roleView: RoleViewValue = ROLE_VIEW_ALL,
    options: ScopeTokenSwitchOptions = {},
    preference?: RoleViewPreferenceV2,
  ) => {
    const {
      restorePreferredRole: _restorePreferredRole,
      ...requestOptions
    } = options
    const rollbackScopeSwitch = createScopeSwitchRollback()
    let appliedSessionToken = ""
    const exchangeScopeSession = async (
      targetRoleView: RoleViewValue,
      targetPreference?: RoleViewPreferenceV2,
    ) => {
      const roleId = targetRoleView === ROLE_VIEW_ALL ? undefined : Number(targetRoleView)
      if (roleId !== undefined && !Number.isFinite(roleId)) {
        throw new Error("角色视图非法")
      }
      const authMutationId = beginAuthMutation()
      const ticketResponse = await switchOrganizationScope(
        {
          scopeId,
          ...(roleId !== undefined ? { roleId } : {}),
        },
        requestOptions,
      )
      assertAuthMutationCurrent(authMutationId)
      const issuedScope = ticketResponse.data?.currentScope
      let expectedMerchantUserIds: number[] | undefined
      if (issuedScope) {
        if (issuedScope.scopeId !== scopeId) {
          throw new Error("机构切换返回的组织范围不一致")
        }
        const expectedScopeType = scopeId.startsWith("chain-headquarter-")
          ? "CHAIN_HEADQUARTER"
          : scopeId.startsWith("chain-region-")
            ? "CHAIN_REGION"
            : scopeId.startsWith("chain-store-")
              ? "CHAIN_STORE"
              : scopeId.startsWith("shop-")
                ? "SHOP"
                : undefined
        if (!expectedScopeType || issuedScope.scopeType !== expectedScopeType) {
          throw new Error("机构切换返回的组织类型不一致")
        }
        if (roleId !== undefined && issuedScope.roleId !== roleId) {
          throw new Error("机构切换返回的角色范围不一致")
        }
        if (roleId === undefined && issuedScope.roleId != null) {
          throw new Error("机构切换未返回全部角色范围")
        }
        expectedMerchantUserIds = [
          issuedScope.merchantUserId,
          ...(issuedScope.merchantUserIds ?? []),
        ]
          .map(value => Number(value))
          .filter(value => Number.isSafeInteger(value) && value >= 0)
        if (expectedMerchantUserIds.length === 0) {
          throw new Error("机构切换未返回用户身份范围")
        }
      }
      const authCode = ticketResponse.data?.authCode
      if (!authCode) {
        throw new Error("机构切换未返回授权码")
      }
      let loginResponse: LoginUserInfo
      try {
        loginResponse = await loginApi(authCode, {
          ...requestOptions,
          client: "teashop_merchant",
        })
      } catch (error) {
        assertAuthMutationCurrent(authMutationId)
        // A command that issues a ticket must never be used as a read-only
        // confirmation. Preserve the remembered role on an opaque OAuth failure;
        // a retry will validate it before issuing the next ticket.
        throw error
      }
      assertAuthMutationCurrent(authMutationId)
      applyLoginSession(loginResponse, { oauthClient: "teashop_merchant" })
      appliedSessionToken = loginResponse.access_token
      markObservedScopeToken(scopeId, loginResponse.access_token)
      const existingPreference = targetPreference
        ?? readRoleViewPreference(scopeId, getRoleViewCorpUserId())
      issuedRoleView = {
        scopeId,
        value: targetRoleView,
        expectedCorpUserId: existingPreference?.corpUserId ?? getRoleViewCorpUserId(),
        expectedMerchantUserIds,
        usesLegacyTicket: !issuedScope,
      }
    }

    try {
      const initialPreference = preference
        ?? readRoleViewPreference(scopeId, getRoleViewCorpUserId())
      if (roleView !== ROLE_VIEW_ALL) {
        try {
          await exchangeScopeSession(roleView, initialPreference)
          return roleView
        } catch (error) {
          if (
            options.restorePreferredRole !== true
            || !isHeadquartersScopeRoleUnavailableError(error)
          ) {
            throw error
          }
          clearRoleViewPreference(scopeId, initialPreference?.corpUserId)
          await exchangeScopeSession(ROLE_VIEW_ALL)
          return ROLE_VIEW_ALL
        }
      }

      await exchangeScopeSession(ROLE_VIEW_ALL, preference)
      if (preference || options.restorePreferredRole !== true) {
        return ROLE_VIEW_ALL
      }

      const corpUserId = getRoleViewCorpUserId()
      const rememberedPreference = readRoleViewPreference(scopeId, corpUserId)
      if (!rememberedPreference || rememberedPreference.roleView === ROLE_VIEW_ALL) {
        return ROLE_VIEW_ALL
      }
      try {
        await exchangeScopeSession(rememberedPreference.roleView, rememberedPreference)
        return rememberedPreference.roleView
      } catch (error) {
        if (!isHeadquartersScopeRoleUnavailableError(error)) {
          throw error
        }
        clearRoleViewPreference(scopeId, rememberedPreference.corpUserId)
        return ROLE_VIEW_ALL
      }
    } catch (error) {
      if (appliedSessionToken) {
        rollbackScopeSwitch(appliedSessionToken)
      }
      throw error
    }
  }

  /** 切换角色 */
  const changeRoles = async (role: string) => {
    const roleView = role as RoleViewValue
    const viewScopeStore = useViewScopeStoreHook()
    const activeScopeId = viewScopeStore.activeScopeId
    if (!activeScopeId) {
      throw new Error("请先选择机构")
    }
    await switchBusinessContext({
      scopeId: activeScopeId,
      resolveScopeId: () => viewScopeStore.activeScopeId,
      roleView,
      strictRoleView: true,
      route: router.currentRoute.value,
      router,
      resetRouter,
      viewScopeStore,
      chainScopeStore: useChainScopeStore(),
      userStore: {
        switchScopeToken,
        prepareBusinessContext,
        getInfo,
        fetchShopInfo,
        getActiveRoleList: () => activeRoleList.value,
        getActivePermissionList: resolveActiveRolePermissionListForScope,
        getActiveRoleView: () => activeRoleView.value,
        getDefaultHomePath: () => activeRoleDefaultHomePath.value,
        getPreferredRoleView: readPreferredRoleView,
        stageActiveRoleView,
        setActiveRoleView,
        syncBusinessWebSocket,
        disconnectBusinessWebSocket,
        clearScopedPermissionState,
        createScopeSwitchRollback,
        createRoleViewPreferenceRollback,
        getBusinessContextAccountId: () =>
          resolveScopeAccountId(userInfo.value?.corpUserId, userInfo.value?.phone),
      },
      permissionStore,
      tagsViewStore,
      getToken,
    })
  }

  /** 登出 */
  const logout = () => {
    invalidateAuthSession()
    const logoutToken = getToken()
    Promise.resolve(logoutApi(logoutToken)).catch((error) => {
      console.warn("服务端登出失败，本地会话已清理:", error)
    })
    removeToken()
    removeRefreshToken()
    useViewScopeStoreHook().reset({}, "user.logout")
    markObservedAuthToken("")
    resetLocalAccountSession()
    token.value = ""
    refreshToken.value = ""
    setOAuthClient("teashop")
  }

  /** 重置 Token */
  const resetToken = () => {
    invalidateAuthSession()
    const logoutToken = getToken()
    Promise.resolve(logoutApi(logoutToken)).catch((error) => {
      console.warn("服务端登出失败，本地会话已清理:", error)
    })
    removeToken()
    removeRefreshToken()
    useViewScopeStoreHook().reset({}, "user.reset-token")
    markObservedAuthToken("")
    resetLocalAccountSession()
    token.value = ""
    refreshToken.value = ""
    setOAuthClient("teashop")
  }

  /** 重置 visited views 和 cached views */
  const _resetTagsView = () => {
    tagsViewStore.delAllVisitedViews()
    tagsViewStore.delAllCachedViews()
  }

  const getUserInfo = () => {
    return userInfo.value
  }

  const setUserInfo = (value: MerchantUserType) => {
    userInfo.value = value
  }

  const setShopInfo = (value: ShopInfoType) => {
    shopInfo.value = value
  }

  const getShopInfo = () => {
    return shopInfo.value
  }

  const checkPermission = (value: string | string[]) => {
    if (Array.isArray(value)) {
      return activeRolePermissionList.value.some((item) => value.includes(item))
    }
    return activeRolePermissionList.value.includes(value)
  }

  const checkParentPermission = (value: string) => {
    return activeRolePermissionList.value.some((item) => item.indexOf(value) > -1)
  }

  const checkStockPermission = (value: string) => {
    return activeRolePermissionList.value.some((item) => item.startsWith(value))
  }

  const setPrintSetting = (value: UserPrintSetting) => {
    printSetting.value = normalizeUserPrintSetting(value)
  }

  const setHandoverDetail = (val: any) => {
    HandoverDetail.value = val
  }

  const setOpen = (val: boolean, targetShopId = shopId.value) => {
    setShiftStatus(val ? "open" : "closed", targetShopId)
  }

  return {
    token,
    roles,
    roleViewAvailable,
    username,
    shopId,
    setRoles,
    businessRoles,
    setBusinessRoles,
    activeRoleView,
    roleViewOptions,
    setActiveRoleView,
    stageActiveRoleView,
    getPreferredRoleView: readPreferredRoleView,
    organizationSelectionRequired,
    clearOrganizationSelectionRequired,
    getPermissionList: () => permissionList.value,
    getActivePermissionList: resolveActiveRolePermissionListForScope,
    getActiveRoleList: () => activeRoleList.value,
    getActiveRoleView: () => activeRoleView.value,
    getActiveRoleProfile: () => activeRoleProfile.value,
    getDefaultHomePath: () => activeRoleDefaultHomePath.value,
    checkPermission,
    login,
    switchScopeToken,
    createScopeSwitchRollback,
    createRoleViewPreferenceRollback,
    getBusinessContextAccountId: () =>
      resolveScopeAccountId(userInfo.value?.corpUserId, userInfo.value?.phone),
    clearScopedPermissionState,
    prepareBusinessContext,
    getInfo,
    syncBusinessWebSocket,
    disconnectBusinessWebSocket,
    fetchShopInfo,
    changeRoles,
    userLoginInfo,
    getUserInfo,
    setUserInfo,
    getShopInfo,
    setShopInfo,
    printSetting,
    setPrintSetting,
    logout,
    resetToken,
    wxLogin,
    refreshTokenRequest,
    checkParentPermission,
    checkStockPermission,
    setHandoverDetail,
    HandoverDetail,
    shiftStatusSnapshot,
    setShiftStatus,
    getShiftStatusForShop,
    resetShiftStatus,
    isOpen,
    setOpen,
    shopName,
  }
})

/** 在 setup 外使用 */
export function useUserStoreHook() {
  return useUserStore(store)
}
